import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/utils-date";
import { useAuth } from "@/context/AuthContext";
import { setMarginPreferenceCache } from "@/lib/marginPreference";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Bell, Mail, MessageSquare, Clock, PackageX, Info, RefreshCw, Send, History, CheckCircle2, CalendarCheck, PlayCircle, Download, Trash2, ShieldCheck, TrendingUp } from "lucide-react";
import InviteCodesAdmin from "@/components/InviteCodesAdmin";

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuest = !user;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  // --- Svuota magazzino (ex "reset dati demo") ------------------------------
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeCounts, setWipeCounts] = useState(null); // {products, sales, suppliers, lots, is_empty}
  const [wipeWord, setWipeWord] = useState("");
  const [wiping, setWiping] = useState(false);

  // Carica i conteggi all'apertura della pagina, così il pulsante "Ricarica
  // dati dimostrativi" appare solo se il magazzino è già vuoto.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const counts = await api.resetPreview();
        if (!cancelled) setWipeCounts(counts);
      } catch (_e) { /* silente: se fallisce si nasconde la sezione condizionale */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const [s, setS] = useState({
    in_app: true,
    email: false,
    sms: false,
    email_address: "",
    phone_number: "",
    days_before_expiry: 7,
    stagnant_days: 30,
    auto_apply_suggested_expiry: true,
    show_margin: true,
  });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.getNotificationSettings().then((d) =>
      setS({
        in_app: d.in_app ?? true,
        email: d.email ?? false,
        sms: d.sms ?? false,
        email_address: d.email_address || "",
        phone_number: d.phone_number || "",
        days_before_expiry: d.days_before_expiry ?? 7,
        stagnant_days: d.stagnant_days ?? 30,
        auto_apply_suggested_expiry: d.auto_apply_suggested_expiry ?? true,
        show_margin: d.show_margin ?? true,
      })
    );
    api.listNotificationLogs(10).then(setLogs).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateNotificationSettings({
        ...s,
        days_before_expiry: Number(s.days_before_expiry) || 7,
        stagnant_days: Number(s.stagnant_days) || 30,
        auto_apply_suggested_expiry: !!s.auto_apply_suggested_expiry,
        show_margin: !!s.show_margin,
      });
      // Update the in-memory cache so MarginPill instances re-render immediately
      setMarginPreferenceCache(!!s.show_margin);
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  // Serializzazione dei PATCH per singolo campo: tap ripetuti veloci sullo stesso
  // toggle vengono accodati, non lanciati in parallelo. Evita che un response HTTP
  // lento sovrascriva uno stato più recente.
  const inflightRef = useRef({});

  // Salvataggio istantaneo dei toggle boolean via PATCH parziale (solo il campo
  // cambiato). Motivo del PATCH invece del PUT: se l'utente sta digitando in
  // email_address / phone_number / days_before_expiry / stagnant_days e nel
  // frattempo tocca un toggle, il PUT dell'oggetto intero committerebbe quei
  // campi in editing come side-effect. Con il PATCH parziale non succede.
  // Il pulsante "Salva" resta per i campi testo/numerici.
  const persistToggle = (field, value) => {
    const previous = s[field];
    setS((p) => ({ ...p, [field]: value }));
    if (field === "show_margin") setMarginPreferenceCache(!!value);
    const prev = inflightRef.current[field] || Promise.resolve();
    const next = prev
      .catch(() => {}) // isola le catene: un errore precedente non blocca il tap successivo
      .then(() => api.patchNotificationSettings({ [field]: value }))
      .catch((err) => {
        setS((p) => ({ ...p, [field]: previous }));
        if (field === "show_margin") setMarginPreferenceCache(!!previous);
        toast.error("Impostazione non salvata", {
          description: err?.response?.data?.detail || err.message,
        });
      });
    inflightRef.current[field] = next;
    return next;
  };

  const sendNow = async () => {
    if (!s.email_address) {
      toast.error("Inserisci e salva l'email destinatario prima di inviare");
      return;
    }
    setSending(true);
    try {
      const res = await api.sendTestNotification({});
      toast.success("Email inviata!", {
        description: `${res.alert_count} avvisi inclusi nel riepilogo`,
      });
      const newLogs = await api.listNotificationLogs(10);
      setLogs(newLogs);
    } catch (err) {
      toast.error("Invio fallito", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSending(false);
    }
  };

  const resetDemo = async () => {
    // Vecchio comportamento sostituito: apri il dialog "Svuota magazzino".
    try {
      const counts = await api.resetPreview();
      setWipeCounts(counts);
      setWipeWord("");
      setWipeOpen(true);
    } catch (err) {
      toast.error("Impossibile leggere i conteggi", { description: err?.response?.data?.detail || err.message });
    }
  };

  const reloadDemoSeed = async () => {
    // Ricarica dei dati dimostrativi: consentita solo se il magazzino è vuoto.
    // Il backend rifiuta comunque con 400 se manca la conferma, quindi la
    // passiamo qui esplicitamente. La ricarica scatta solo se was_empty=True.
    try {
      const r = await api.resetDemo("SVUOTA", true);
      if (r.seeded) {
        toast.success("Dati demo caricati");
      } else {
        toast.info("Il magazzino aveva dati veri: è stato svuotato senza ricaricare demo");
      }
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || err.message });
    }
  };

  const confirmWipe = async () => {
    if (wipeWord !== "SVUOTA") return;
    setWiping(true);
    try {
      // seed=true: il backend seed solo se il magazzino era vuoto (invariante lato server).
      await api.resetDemo("SVUOTA", true);
      toast.success("Magazzino svuotato");
      setWipeOpen(false);
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || err.message });
    } finally {
      setWiping(false);
    }
  };

  const replayTour = async () => {
    try {
      await api.onboardingRestart();
      try {
        localStorage.removeItem("mag_onboarding_done");
        localStorage.setItem("mag_onboarding_force", "1");
      } catch (_e) { /* noop */ }
      toast.success("Riprendiamo il tour dalla Dashboard...");
      setTimeout(() => {
        window.location.href = "/app/dashboard";
      }, 500);
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || err.message });
    }
  };

  const exportAccount = async () => {
    try {
      const data = await api.exportAccount();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `inpalmo-export-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export completato", { description: "I tuoi dati sono stati scaricati in JSON." });
    } catch (err) {
      toast.error("Errore export", { description: err?.response?.data?.detail || err.message });
    }
  };

  const deleteAccountConfirmed = async () => {
    setDeleting(true);
    try {
      await api.deleteAccount();
      try {
        localStorage.clear();
      } catch (_e) { /* noop */ }
      toast.success("Account eliminato", { description: "Tutti i tuoi dati sono stati cancellati." });
      setTimeout(() => {
        window.location.href = "/";
      }, 800);
    } catch (err) {
      toast.error("Errore", { description: err?.response?.data?.detail || err.message });
      setDeleting(false);
    }
  };

  return (
    <div data-testid="settings-page" className="max-w-3xl space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8E918C] mb-2">Configurazione</p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans, sans-serif" }}>
          Impostazioni
        </h1>
        <p className="text-[#5C5F5A] mt-2">Scegli come e quando ricevere gli avvisi.</p>
      </div>

      {/* Notifiche */}
      <div className="mag-card p-6 space-y-5">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Canali di notifica
          </h2>
          <p className="text-sm text-[#8E918C] mt-1">Scegli dove vuoi ricevere gli avvisi.</p>
        </div>

        <ChannelToggle
          icon={Bell}
          title="Notifiche in-app"
          description="Avvisi all'interno dell'app, visibili dalla campanella"
          checked={s.in_app}
          onChange={(v) => persistToggle("in_app", v)}
          testid="toggle-in-app"
          badge="Attivo"
          badgeColor="bg-[#E8F3EC] text-[#2B5A3F] border-[#C5E1D1]"
        />

        <ChannelToggle
          icon={Mail}
          title="Email"
          description="Riepilogo automatico ogni sera alle 21:00 + avvisi urgenti"
          checked={s.email}
          onChange={(v) => setS((p) => ({ ...p, email: v }))}
          testid="toggle-email"
          badge="Attivo"
          badgeColor="bg-[#E8F3EC] text-[#2B5A3F] border-[#C5E1D1]"
        >
          {s.email && (
            <div className="mt-3 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">
                  Indirizzo email
                </Label>
                <Input
                  data-testid="email-address-input"
                  type="email"
                  placeholder="tua@email.com"
                  value={s.email_address}
                  onChange={(e) => setS((p) => ({ ...p, email_address: e.target.value }))}
                />
              </div>
              <Button
                type="button"
                data-testid="send-test-email-btn"
                onClick={sendNow}
                disabled={sending || !s.email_address}
                variant="outline"
                size="sm"
                className="border-[#C5E1D1] bg-[#E8F3EC] text-[#2B5A3F] hover:bg-[#D3E8DA]"
              >
                <Send size={14} className="mr-2" />
                {sending ? "Invio in corso..." : "Invia ora un riepilogo"}
              </Button>
            </div>
          )}
        </ChannelToggle>

        <ChannelToggle
          icon={MessageSquare}
          title="SMS"
          description="Ricevi alert urgenti via SMS sul telefono"
          checked={s.sms}
          onChange={(v) => persistToggle("sms", v)}
          testid="toggle-sms"
          badge="Disponibile a breve"
          badgeColor="bg-[#FDF6E3] text-[#8B6518] border-[#F2DEB0]"
        >
          {s.sms && (
            <div className="mt-3 space-y-1.5">
              <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">
                Numero di telefono
              </Label>
              <Input
                data-testid="phone-input"
                type="tel"
                placeholder="+39 333 1234567"
                value={s.phone_number}
                onChange={(e) => setS((p) => ({ ...p, phone_number: e.target.value }))}
              />
            </div>
          )}
        </ChannelToggle>

        {(s.sms) && (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-[#EDF5F9] border border-[#CFE4F0]">
            <Info size={18} className="text-[#2F5A78] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#2F5A78]">
              <strong>SMS:</strong> in arrivo. Per attivare gli SMS reali serve la configurazione di Twilio. Le preferenze vengono comunque salvate.
            </p>
          </div>
        )}

        {s.email && (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-[#E8F3EC] border border-[#C5E1D1]">
            <CheckCircle2 size={18} className="text-[#2B5A3F] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#2B5A3F]">
              <strong>Email attiva via Resend.</strong> Riceverai un riepilogo automatico ogni sera alle 21:00 (ora italiana) con vendite del giorno e totale incassato, e potrai inviare un riepilogo manualmente in qualsiasi momento dal pulsante &quot;Invia ora&quot;.
            </p>
          </div>
        )}
      </div>

      {/* Soglie */}
      <div className="mag-card p-6 space-y-5">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Soglie di avviso
          </h2>
          <p className="text-sm text-[#8E918C] mt-1">Personalizza quando un prodotto entra in allerta.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-[#F4F1ED] border border-[#EAE7E1]">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-[#2E4A3B]" />
              <Label className="text-sm font-medium">Giorni prima della scadenza</Label>
            </div>
            <p className="text-xs text-[#8E918C] mb-3">
              Mostra avviso quando mancano N giorni alla scadenza
            </p>
            <Input
              data-testid="days-before-expiry-input"
              type="number"
              min="1"
              max="90"
              value={s.days_before_expiry}
              onChange={(e) => setS((p) => ({ ...p, days_before_expiry: e.target.value }))}
              className="bg-white"
            />
          </div>

          <div className="p-4 rounded-lg bg-[#F4F1ED] border border-[#EAE7E1]">
            <div className="flex items-center gap-2 mb-2">
              <PackageX size={16} className="text-[#4A7B9D]" />
              <Label className="text-sm font-medium">Soglia prodotti fermi (giorni)</Label>
            </div>
            <p className="text-xs text-[#8E918C] mb-3">
              Dopo quanti giorni senza vendite considerare un prodotto &quot;fermo&quot;
            </p>
            <Input
              data-testid="stagnant-days-input"
              type="number"
              min="7"
              max="365"
              value={s.stagnant_days}
              onChange={(e) => setS((p) => ({ ...p, stagnant_days: e.target.value }))}
              className="bg-white"
            />
          </div>
        </div>

        <Button
          data-testid="save-settings-btn"
          onClick={save}
          disabled={saving}
          className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
        >
          {saving ? "Salvataggio..." : "Salva impostazioni"}
        </Button>
      </div>

      {/* AI / Scanner — Suggested expiry */}
      <div className="mag-card p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Scanner AI · Scadenze suggerite
          </h2>
          <p className="text-sm text-[#8E918C] mt-1">
            Durante lo scan di scontrini/DDT, l&apos;AI stima una scadenza tipica per i prodotti senza data esplicita.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 p-4 rounded-lg bg-[#F4F1ED] border border-[#EAE7E1]">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="h-9 w-9 rounded-lg bg-[#E8F3EC] text-[#2B5A3F] flex items-center justify-center flex-shrink-0">
              <CalendarCheck size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <Label htmlFor="auto-apply-expiry" className="text-sm font-medium cursor-pointer">
                Applica automaticamente la scadenza suggerita
              </Label>
              <p className="text-xs text-[#8E918C] mt-0.5">
                Se attivo: ogni riga senza scadenza viene pre-riempita con la stima AI. Puoi sempre modificarla.
                Se spento: l&apos;AI mostra la suggestion ma la applichi tu manualmente (più sicuro per inventari delicati).
              </p>
            </div>
          </div>
          <Switch
            id="auto-apply-expiry"
            data-testid="auto-apply-expiry-switch"
            checked={s.auto_apply_suggested_expiry}
            onCheckedChange={(v) => persistToggle("auto_apply_suggested_expiry", v)}
          />
        </div>
      </div>

      {/* Margine / Markup display */}
      <div data-testid="margin-settings-card" className="mag-card p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Margine · Calcolo percentuali
          </h2>
          <p className="text-sm text-[#8E918C] mt-1">
            L&apos;app può calcolare e mostrare automaticamente il margine % (es. <strong>+54%</strong>) sui prodotti, confrontando prezzo di vendita e costo d&apos;acquisto.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 p-4 rounded-lg bg-[#F4F1ED] border border-[#EAE7E1]">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="h-9 w-9 rounded-lg bg-[#E8F3EC] text-[#2B5A3F] flex items-center justify-center flex-shrink-0">
              <TrendingUp size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <Label htmlFor="show-margin" className="text-sm font-medium cursor-pointer">
                Mostra il margine % sui prodotti
              </Label>
              <p className="text-xs text-[#8E918C] mt-0.5">
                Se attivo: vedrai un pill colorato (es. <strong className="text-[#2B5A3F]">+54%</strong>) accanto al prezzo di ogni prodotto e nei dettagli, utile per ottimizzare la marginalità.
                Se spento: l&apos;app non mostra alcuna percentuale — perfetto se usi InPalmo solo per scadenze e giacenze, senza ragionare sui prezzi.
              </p>
            </div>
          </div>
          <Switch
            id="show-margin"
            data-testid="show-margin-switch"
            checked={s.show_margin}
            onCheckedChange={(v) => persistToggle("show_margin", v)}
          />
        </div>
      </div>

      {/* Notification logs */}
      {logs.length > 0 && (
        <div className="mag-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <History size={18} className="text-[#2E4A3B]" />
            <h2 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
              Ultime notifiche inviate
            </h2>
          </div>
          <div className="space-y-2">
            {logs.map((log, i) => (
              <div
                key={i}
                data-testid={`notification-log-${i}`}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#EAE7E1] bg-[#F9F8F6]"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="h-8 w-8 rounded-lg bg-[#E8F3EC] text-[#2B5A3F] flex items-center justify-center flex-shrink-0">
                    <Mail size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{log.subject}</div>
                    <div className="text-xs text-[#8E918C] mt-0.5">
                      {log.recipient} · {formatDateTime(log.sent_at)}
                    </div>
                  </div>
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md bg-[#E8F3EC] text-[#2B5A3F] border border-[#C5E1D1] flex-shrink-0">
                  {log.alert_count} avvis{log.alert_count === 1 ? "o" : "i"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Demo */}
      <div className="mag-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
              Dati demo & Tour
            </h2>
            <p className="text-sm text-[#8E918C] mt-1">
              Rivedi il tour di benvenuto o azzera il magazzino.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="replay-tour-btn"
              onClick={replayTour}
              variant="outline"
              className="border-[#C5E1D1] bg-[#E8F3EC] text-[#2B5A3F] hover:bg-[#D3E8DA]"
            >
              <PlayCircle size={16} className="mr-2" />
              Rivedi tour di benvenuto
            </Button>
            {/* "Ricarica dati dimostrativi" compare SOLO se il magazzino è vuoto */}
            {wipeCounts?.is_empty && (
              <Button
                data-testid="reload-demo-btn"
                onClick={reloadDemoSeed}
                variant="outline"
                className="border-[#C5E1D1] bg-[#E8F3EC] text-[#2B5A3F] hover:bg-[#D3E8DA]"
              >
                <RefreshCw size={16} className="mr-2" />
                Ricarica dati dimostrativi
              </Button>
            )}
            <div className="flex flex-col items-end">
              <Button
                data-testid="wipe-store-btn"
                onClick={resetDemo}
                className="bg-[#8E3B2A] hover:bg-[#6E2A1E] text-white"
              >
                <Trash2 size={16} className="mr-2" />
                Svuota il magazzino
              </Button>
              <span className="text-[11px] text-[#8E3B2A] mt-1">
                Cancella tutto e non si torna indietro
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog di conferma svuotamento magazzino */}
      <Dialog open={wipeOpen} onOpenChange={setWipeOpen}>
        <DialogContent data-testid="wipe-store-dialog">
          <DialogHeader>
            <DialogTitle>Svuota il magazzino</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 text-sm">
                <div className="text-[#3E4034]">
                  Stai per cancellare:{" "}
                  <strong>
                    {wipeCounts?.products || 0} prodotti, {wipeCounts?.sales || 0} vendite,{" "}
                    {wipeCounts?.lots || 0} lotti, {wipeCounts?.suppliers || 0} fornitori
                  </strong>
                  .
                </div>
                <div className="text-[#8E3B2A]">
                  L&apos;operazione non si annulla. Se non hai un backup recente,{" "}
                  <button
                    type="button"
                    onClick={() => { setWipeOpen(false); navigate("/settings#export"); }}
                    className="underline font-medium text-[#8E3B2A] hover:text-[#6E2A1E]"
                    data-testid="wipe-export-link"
                  >
                    esporta i miei dati
                  </button>{" "}
                  prima di procedere.
                </div>
                <div className="pt-2">
                  <Label htmlFor="wipe-word-input" className="text-[13px] text-[#5C5F5A]">
                    Per procedere, scrivi <code className="font-mono font-semibold text-[#8E3B2A]">SVUOTA</code>:
                  </Label>
                  <Input
                    id="wipe-word-input"
                    data-testid="wipe-word-input"
                    value={wipeWord}
                    onChange={(e) => setWipeWord(e.target.value)}
                    className="mt-1"
                    autoComplete="off"
                    placeholder="SVUOTA"
                  />
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setWipeOpen(false)}
              disabled={wiping}
              data-testid="wipe-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              type="button"
              onClick={confirmWipe}
              disabled={wiping || wipeWord !== "SVUOTA"}
              className="bg-[#8E3B2A] hover:bg-[#6E2A1E] text-white"
              data-testid="wipe-confirm-btn"
            >
              {wiping ? "Svuotamento..." : "Svuota definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Codici invito (admin only) */}
      {user?.is_admin && <InviteCodesAdmin />}

      {/* Account & Privacy (GDPR) */}
      <div data-testid="account-section" className="mag-card p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-[#E8F3EC] text-[#2B5A3F] flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={18} />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
              Account e Privacy
            </h2>
            <p className="text-sm text-[#8E918C] mt-1">
              I tuoi dati sono tuoi. Scaricali quando vuoi o cancella tutto in qualsiasi momento.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={exportAccount}
            disabled={isGuest}
            data-testid="export-account-btn"
            className="flex items-start gap-3 p-4 rounded-xl border border-[#EAE7E1] bg-white hover:border-[#2E4A3B] hover:bg-[#F4F1ED] disabled:opacity-50 disabled:cursor-not-allowed transition-all text-left"
          >
            <Download size={18} className="text-[#2E4A3B] flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#1A1C19]">Esporta i miei dati</div>
              <div className="text-xs text-[#5C5F5A] mt-0.5">
                Scarica un file JSON con prodotti, vendite, fornitori e impostazioni.
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={isGuest}
            data-testid="delete-account-btn"
            className="flex items-start gap-3 p-4 rounded-xl border border-[#F5C7BD] bg-[#FCEAE8]/40 hover:border-[#C65D47] hover:bg-[#FCEAE8] disabled:opacity-50 disabled:cursor-not-allowed transition-all text-left"
          >
            <Trash2 size={18} className="text-[#C65D47] flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#1A1C19]">Elimina account</div>
              <div className="text-xs text-[#5C5F5A] mt-0.5">
                Cancella permanentemente account, negozio e tutti i dati associati.
              </div>
            </div>
          </button>
        </div>

        {isGuest && (
          <p className="text-xs text-[#8E918C] mt-3">
            Questa sezione è disponibile solo per gli account registrati con Google.
          </p>
        )}

        <div className="mt-5 pt-4 border-t border-[#EAE7E1] flex flex-wrap items-center gap-4 text-xs text-[#5C5F5A]">
          <button
            type="button"
            onClick={() => navigate("/privacy")}
            className="hover:text-[#2E4A3B] hover:underline"
            data-testid="settings-privacy-link"
          >
            Informativa privacy
          </button>
          <button
            type="button"
            onClick={() => navigate("/termini")}
            className="hover:text-[#2E4A3B] hover:underline"
            data-testid="settings-terms-link"
          >
            Termini di servizio
          </button>
          <a href="mailto:ciao@inpalmo.it" className="hover:text-[#2E4A3B] hover:underline ml-auto">
            ciao@inpalmo.it
          </a>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        open={confirmDelete}
        onOpenChange={(o) => {
          if (!deleting) {
            setConfirmDelete(o);
            if (!o) setDeleteText("");
          }
        }}
      >
        <DialogContent data-testid="delete-account-dialog" className="max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#FCEAE8] text-[#C65D47] flex items-center justify-center flex-shrink-0">
                <Trash2 size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-left" style={{ fontFamily: "Work Sans, sans-serif" }}>
                  Eliminare definitivamente l&apos;account?
                </DialogTitle>
                <DialogDescription className="text-left mt-1">
                  Tutti i dati del tuo negozio (prodotti, vendite, fornitori, impostazioni)
                  saranno cancellati in modo permanente. Questa azione non può essere annullata.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="mt-2 space-y-3">
            <div className="p-3 rounded-lg bg-[#FDF6E3] border border-[#F2DEB0] text-[13px] text-[#8B6518]">
              💡 Prima di procedere, ti consigliamo di{" "}
              <button
                type="button"
                onClick={async () => {
                  setConfirmDelete(false);
                  await exportAccount();
                }}
                className="underline font-medium"
              >
                scaricare un export dei tuoi dati
              </button>
              .
            </div>
            <Label htmlFor="confirm-text" className="text-sm">
              Per confermare, scrivi <strong>elimina</strong> qui sotto:
            </Label>
            <Input
              id="confirm-text"
              data-testid="delete-account-confirm-input"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              autoComplete="off"
              autoFocus
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2 mt-2 flex-col-reverse sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDelete(false);
                setDeleteText("");
              }}
              disabled={deleting}
              className="w-full sm:w-auto"
            >
              Annulla
            </Button>
            <Button
              data-testid="delete-account-confirm-btn"
              onClick={deleteAccountConfirmed}
              disabled={deleteText.trim().toLowerCase() !== "elimina" || deleting}
              className="bg-[#C65D47] hover:bg-[#A84B38] text-white w-full sm:w-auto"
            >
              {deleting ? "Eliminazione…" : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChannelToggle({ icon: Icon, title, description, checked, onChange, children, testid, badge, badgeColor }) {
  return (
    <div className="p-4 rounded-lg border border-[#EAE7E1]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3 min-w-0 flex-1">
          <div className="h-10 w-10 rounded-lg bg-[#F4F1ED] text-[#2E4A3B] flex items-center justify-center flex-shrink-0">
            <Icon size={18} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-medium text-sm">{title}</div>
              {badge && (
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md border ${badgeColor}`}>
                  {badge}
                </span>
              )}
            </div>
            <div className="text-xs text-[#8E918C] mt-1">{description}</div>
          </div>
        </div>
        <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
      </div>
      {children}
    </div>
  );
}
